import {useMemo} from 'react';

import {useMultiRunWorkspaceQuery} from '../../state/graphql/multiRunWorkspaceQuery';
import {Grouping} from '../../util/queryTypes';
import {createMultiRunDefaultSpec} from '../Workspace/createMultiRunDefaultSpec';
import {useIsWorkspaceTemplateActionDisplayed} from '../WorkspaceTemplate/useIsWorkspaceTemplateEnabled';
import {useViewerWorkspaceTemplates} from '../WorkspaceTemplate/useViewerWorkspaceTemplates';
import {MultiRunWorkspaceProps, MultiRunWorkspaceReadyProps} from './types';

type UseMultiRunWorkspaceQueryPropsResult =
  | {loading: true; error: null}
  | {loading: false; error: true}
  | {
      loading: false;
      error: null;
      readyProps: MultiRunWorkspaceReadyProps;
    };

export function useMultiRunWorkspaceQueryProps(
  props: MultiRunWorkspaceProps
): UseMultiRunWorkspaceQueryPropsResult {
  const {entityName, projectName, defaultSpecPostFn, viewType} = props;
  // We need to compute the default workspace step, which relies on first
  // loading the project.
  const multiRunWorkspaceQuery = useMultiRunWorkspaceQuery({
    entityName,
    projectName,
  });

  const {isWorkspaceTemplateActionDisplayed, isLoadingEntityType} =
    useIsWorkspaceTemplateActionDisplayed(viewType);
  const {workspaceTemplates, isLoadingWorkspaceTemplates} =
    useViewerWorkspaceTemplates(isWorkspaceTemplateActionDisplayed);

  return useMemo(() => {
    if (
      multiRunWorkspaceQuery.loading ||
      isLoadingWorkspaceTemplates ||
      isLoadingEntityType
    ) {
      return {loading: true, error: null};
    }

    const project = multiRunWorkspaceQuery.data?.project;

    if (project == null) {
      return {loading: false, error: true};
    }

    const recommendedGrouping: Grouping = [];

    let defaultSpec = createMultiRunDefaultSpec(workspaceTemplates[0]);
    defaultSpec.section.runSets![0].grouping = recommendedGrouping;
    if (defaultSpecPostFn != null) {
      defaultSpec = defaultSpecPostFn(defaultSpec);
    }

    return {
      loading: false,
      error: null,
      readyProps: {
        project,
        defaultSpec,
        recommendedGrouping,
      },
    };
  }, [
    multiRunWorkspaceQuery.loading,
    multiRunWorkspaceQuery.data?.project,
    isLoadingWorkspaceTemplates,
    isLoadingEntityType,
    workspaceTemplates,
    defaultSpecPostFn,
  ]);
}
